import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient, useLazyQuery } from '@apollo/client';

import pollQuery from './contactBulkImportPoll.graphql';

const POLLING_INTERVAL = 3000;

// Statuses meaning the import finished (VALID_STATUSES minus PROCESSING).
const TERMINAL_STATUSES = ['SUCCESS', 'PARTIAL_SUCCESS', 'FAILURE'];

// Tolerate transient poll errors; give up only after several in a row.
const MAX_CONSECUTIVE_ERRORS = 3;
// Cap runaway polling. ~5 min at the 3s interval — large imports can run for minutes.
const MAX_POLL_ATTEMPTS = 100;

export const usePollContactBulkImportStatus = () => {
  const apolloClient = useApolloClient();

  const consecutiveErrorsRef = useRef(0);
  const attemptsRef = useRef(0);
  const [gaveUp, setGaveUp] = useState(false);

  const [fetchImport, { data, called, stopPolling }] = useLazyQuery<
    ContactBulkImportPollQuery,
    ContactBulkImportPollQueryVariables
  >(pollQuery, {
    fetchPolicy: 'network-only',
    pollInterval: POLLING_INTERVAL,
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      consecutiveErrorsRef.current = 0;
      attemptsRef.current += 1;
      if (attemptsRef.current >= MAX_POLL_ATTEMPTS) setGaveUp(true);
    },
    onError: () => {
      consecutiveErrorsRef.current += 1;
      if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
        setGaveUp(true);
      }
    },
  });

  const startPolling = useCallback(
    (id: string) => {
      fetchImport({ variables: { id } });
    },
    [fetchImport],
  );

  const status = data?.contactBulkImport?.status;
  const reportUrl = data?.contactBulkImport?.reportUrl ?? undefined;
  const failedRecords = data?.contactBulkImport?.failedRecords ?? [];
  const summary = data?.contactBulkImport?.summary ?? undefined;

  const reachedTerminalStatus = !!status && TERMINAL_STATUSES.includes(status);
  const pollingFinished = called && (reachedTerminalStatus || gaveUp);

  useEffect(() => {
    if (pollingFinished) {
      stopPolling();
      if ((summary?.successful ?? 0) > 0) {
        apolloClient.refetchQueries({
          include: [
            'AccountContacts',
            'ManagedPlatformAccountContacts',
            'DepotContacts',
          ],
        });
      }
    }
  }, [pollingFinished, stopPolling, apolloClient, summary]);

  // Stop polling if the modal closes mid-import.
  useEffect(() => () => stopPolling(), [stopPolling]);

  return {
    startPolling,
    summary,
    reportUrl,
    failedRecords,
    pollingFinished,
    // Stopped without a terminal status (errors/cap) — outcome unknown.
    failedToResolve: gaveUp && !reachedTerminalStatus,
  };
};
